import { ApiParams, prepareBaseListApiParams } from '@/api/helpers/apiHelpers';
import { QueryParamsType } from '@/api/helpers/apiParamsHelpers';
import { prepareResponse, throwResponseError, withPreparedResponse } from '@/helpers/apiHelpers/responseHelpers';
import { IDEMPOTENCY_KEY, idempotencyKeyHandler } from '@/helpers/apiHelpers/requestHelpers';
import { getInvoiceLinksApi } from '@/api/v1/organization/invoice-vault/getInvoiceLinksApi';
import { getInvoiceLinkApi } from '@/api/v1/organization/invoice-vault/getInvoiceLinkApi';
import {
  createInvoiceLinkApi,
  CreateInvoiceLinkPayloadType,
} from '@/api/v1/organization/invoice-vault/createInvoiceLinkApi';
import { invoiceLinkActionApi, InvoiceLinkActionType } from '@/api/v1/organization/invoice-vault/invoiceLinkActionApi';
import { getInvoiceChecksApi } from '@/api/v1/organization/invoice-vault/getInvoiceChecksApi';
import { uploadInvoiceFileApi } from '@/api/v1/organization/invoice-vault/uploadInvoiceFileApi';
import {
  createInvoiceCheckApi,
  CreateInvoiceCheckPayloadType,
} from '@/api/v1/organization/invoice-vault/createInvoiceCheckApi';
import {
  getInvoiceCheckVerificationApi,
  invoiceCheckActionApi,
  InvoiceCheckActionType,
  unmaskInvoiceCheckApi,
} from '@/api/v1/organization/invoice-vault/invoiceCheckActionApi';
import {
  connectPlatformApi,
  disconnectPlatformApi,
  getPlatformApi,
  PlatformSettingsPayloadType,
  updatePlatformSettingsApi,
} from '@/api/v1/organization/invoice-vault/platformApi';
import {
  createWebhookApi,
  deleteWebhookApi,
  getWebhookDeliveriesApi,
  getWebhooksApi,
  rotateWebhookSecretApi,
  updateWebhookApi,
  WebhookPayloadType,
} from '@/api/v1/organization/invoice-vault/webhooksApi';

export const INVOICE_VAULT_QUERY_KEYS = {
  links: 'invoice_vault_links',
  link: 'invoice_vault_link',
  checks: 'invoice_vault_checks',
  checkVerification: 'invoice_vault_check_verification',
  platform: ['invoice_vault_platform'],
  webhooks: ['invoice_vault_webhooks'],
  deliveries: 'invoice_vault_webhook_deliveries',
} as const;

export const loadInvoiceLinks = (queryParams: QueryParamsType & { q?: string; status?: string }) => {
  const params: ApiParams = prepareBaseListApiParams(queryParams);

  ['q', 'status'].forEach((key) => {
    if (queryParams?.[key]) params[key] = queryParams[key];
  });

  return withPreparedResponse(() => getInvoiceLinksApi(params));
};

export const loadInvoiceLink = (id: string) => withPreparedResponse(() => getInvoiceLinkApi(id));
export const runInvoiceLinkAction = (id: string, action: InvoiceLinkActionType, payload?: { resolution?: string }) =>
  withPreparedResponse(() => invoiceLinkActionApi(id, action, payload));

export const createInvoiceLink = async (payload: CreateInvoiceLinkPayloadType) => {
  const { header, clearKey } = idempotencyKeyHandler(IDEMPOTENCY_KEY.CREATE_INVOICE_LINK);

  try {
    const response = await createInvoiceLinkApi(payload, header);

    clearKey();

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const loadInvoiceChecks = (queryParams: QueryParamsType & { archived?: string; q?: string }) => {
  const params: ApiParams = prepareBaseListApiParams(queryParams);

  ['archived', 'q'].forEach((key) => {
    if (queryParams?.[key]) params[key] = queryParams[key];
  });

  return withPreparedResponse(() => getInvoiceChecksApi(params));
};

export const uploadInvoiceFile = (file: File) => withPreparedResponse(() => uploadInvoiceFileApi(file));
export const runInvoiceCheckAction = (id: string, action: InvoiceCheckActionType) =>
  withPreparedResponse(() => invoiceCheckActionApi(id, action));
export const unmaskInvoiceCheck = (id: string) => withPreparedResponse(() => unmaskInvoiceCheckApi(id));
export const loadInvoiceCheckVerification = (id: string) =>
  withPreparedResponse(() => getInvoiceCheckVerificationApi(id));

export const createInvoiceCheck = async (payload: CreateInvoiceCheckPayloadType) => {
  const { header, clearKey } = idempotencyKeyHandler(IDEMPOTENCY_KEY.CREATE_INVOICE_CHECK);

  try {
    const response = await createInvoiceCheckApi(payload, header);

    clearKey();

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const loadPlatform = () => withPreparedResponse(getPlatformApi);
export const connectPlatform = (payload: { platform_name: string; api_key_id?: string }) =>
  withPreparedResponse(() => connectPlatformApi(payload));
export const updatePlatformSettings = (payload: PlatformSettingsPayloadType) =>
  withPreparedResponse(() => updatePlatformSettingsApi(payload));
export const disconnectPlatform = () => withPreparedResponse(disconnectPlatformApi);

export const loadWebhooks = () => withPreparedResponse(getWebhooksApi);
export const createWebhook = (payload: WebhookPayloadType) => withPreparedResponse(() => createWebhookApi(payload));
export const updateWebhook = (id: string, payload: WebhookPayloadType) =>
  withPreparedResponse(() => updateWebhookApi(id, payload));
export const deleteWebhook = (id: string) => withPreparedResponse(() => deleteWebhookApi(id));
export const rotateWebhookSecret = (id: string) => withPreparedResponse(() => rotateWebhookSecretApi(id));
export const loadWebhookDeliveries = (id: string) => withPreparedResponse(() => getWebhookDeliveriesApi(id));
